import { Box, IconButton, Modal, ModalDialog, Stack, Typography } from "@mui/joy";
import { useEffect, useState } from "react";
import API_SERVICE from "../../../utils/apiService";
import { convertHoursOfOperation, getDayOfTheWeek, isOpenNow } from "../../../utils/dataFormatter";
import OpenNow from "../../../components/common/OpenNow";
import CloseIcon from '@mui/icons-material/Close';
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setShowViewPlaceModal, setViewPlaceId } from "../../../store";

function ViewPlaceModal() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const chosenPlaceId = useSelector((state) => state.place.viewPlaceId);
  const { placeId } = useParams();
  const showModal = useSelector((state) => state.modal.showViewPlaceModal);

  const [placeData, setPlaceData] = useState(null);
  const hoursOfOperation =
    placeData
      ? placeData.hoursOfOperation
        ? convertHoursOfOperation(placeData["hoursOfOperation"])
        : null
      : null;

  useEffect(() => {
    if (location.pathname.startsWith("/place/")) {
      dispatch(setShowViewPlaceModal(true));
    } else {
      dispatch(setShowViewPlaceModal(false));
      setPlaceData(null);
    }
  }, [location, setPlaceData, dispatch]);

  useEffect(() => {
    if (chosenPlaceId === null) {
      setPlaceData(null);
      return;
    }
    const populatePlaceData = async () => {
      const dataResponse = await API_SERVICE.getPlace(chosenPlaceId);
      setPlaceData(dataResponse);
    };
    populatePlaceData();
  }, [chosenPlaceId]);

  useEffect(() => {
    dispatch(setViewPlaceId(placeId));
  }, [dispatch, placeId]);

  function hoursOfOperationForDay(dayOfWeek, hours, isOpen) {
    if (hours[dayOfWeek].length === 0) {
      return (
        <Stack direction="row">
          <Typography>{dayOfWeek}</Typography>
          <Typography>Closed</Typography>
        </Stack>
      )
    } else {
      let isToday = dayOfWeek === getDayOfTheWeek();
      return (
        hours[dayOfWeek].map((h, i) =>
          <Stack spacing={2} direction="row" key={`{dayOfWeek}-{i}`}>
            <Box sx={{ width: "100px" }}>
              {/* only first row shows day-of-week */}
              <Typography>{i === 0 ? dayOfWeek : ""}</Typography>
            </Box>
            <Box sx={{ width: "180px" }}>
              <Typography>{h}</Typography>
            </Box>
            {/* only first row shows OpenNow */}
            {isToday && (i === 0) && (
              <Box>
                <OpenNow isOpen={isOpen} />
              </Box>
            )}
          </Stack>,
        )
      )
    }
  }

  function closeModal() {
    dispatch(setShowViewPlaceModal(false));
    dispatch(setViewPlaceId(null));
    setPlaceData(null);
    navigate("/");
  }

  return (
    <Modal open={showModal} onClose={() => closeModal()}>
      <ModalDialog sx={{
        overflowY: "auto",
        padding: "40px",
      }}>
        {placeData && (
          <Stack spacing={3}>
            <Stack direction="row-reverse">
              <IconButton size="sm" onClick={() => closeModal()}>
                <CloseIcon></CloseIcon>
              </IconButton>
            </Stack>

            <Typography level="h2">{placeData.name}</Typography>
            <Stack spacing={0}>
              <Typography fontWeight={700}>Corkage Fee & Notes</Typography>
              <Typography fontWeight={700}>${placeData.corkageFee} per bottle</Typography>
              {placeData.bottleLimit && (
                <Typography>limit {placeData.bottleLimit} bottles</Typography>
              )}
            </Stack>

            {placeData.hasWineGlasses
              ? <Typography>✅ This restaurant provides glasses.</Typography>
              : <Typography>❌ This restaurant does not provide glasse.</Typography>
            }

            {placeData.notes && (
              <Stack spacing={0}>
                <Typography fontWeight={700}>Notes</Typography>
                <Typography>{placeData.notes}</Typography>
              </Stack>
            )}

            <Stack spacing={0}>
              <Typography fontWeight={700}>Location & Contact</Typography>
              <Typography>{placeData.formattedAddress}</Typography>
              <Typography>{placeData.formattedPhoneNumber}</Typography>
            </Stack>

            {placeData.acceptsReservations
              ? <Typography>✅ This restaurant accepts reservations.</Typography>
              : <Typography>❌ This restaurant does not accept reservations.</Typography>
            }

            {placeData.hoursOfOperation && (
              <Stack spacing={0}>
                <Typography fontWeight={700}>Hours</Typography>
                {
                  [
                    "Monday",
                    "Tuesday",
                    "Wednesday",
                    "Thursday",
                    "Friday",
                    "Saturday",
                    "Sunday",
                  ].map(day => hoursOfOperationForDay(day, hoursOfOperation, isOpenNow(placeData.hoursOfOperation)))
                }
              </Stack>
            )}

            {placeData.lastUpdatedAt && (
              <Stack spacing={0}>
                <Typography fontWeight={700}>Last Updated</Typography>
                <Typography>
                  {new Date(placeData.lastUpdatedAt).toLocaleDateString()} by {placeData.lastUpdatedBy || 'Unknown'}
                </Typography>
              </Stack>
            )}

          </Stack>
        )}
      </ModalDialog>
    </Modal>
  );
}

export default ViewPlaceModal;
